import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button, Heading, Paragraph, Tag } from '@digdir/designsystemet-react';
import { Routes } from '../../enums/Routes';
import { buildRoute } from '../../utils/routeBuilder';
import { useAnalyzationSummaryQuery } from '../../hooks/queries/useAnalyzationSummaryQuery/useAnalyzationSummaryQuery';
import { useGetAnalyzeSurvey } from '../../hooks/queries/useGetAnalyzeSurvey';
import { agreementOf } from '../../domain/psychological-safety/team-result';
import { GhostLoader } from '../GhostLoader';
import { AnalysisLayout } from '../AnalysisLayout/AnalysisLayout';
import {
  agreementTones,
  changeTone,
  formatChange,
  formatScore,
  getScoreLevel,
  levelTones,
  MAX_SCORE,
  normalizeQuestion,
} from '../AnalysisLayout/analysis.utils';
import classes from './AnalyzationSummary.module.css';

const AgreementTag = ({ standardDeviation }: { standardDeviation?: number }) => {
  const { t } = useTranslation();
  const level = agreementOf(standardDeviation);
  if (!level) return null;
  return (
    <Tag data-size="sm" data-color={agreementTones[level]} className={classes.tag}>
      {t(`teamResult.agreement.${level}`)}
    </Tag>
  );
};

const percent = (score: number) => `${Math.min(100, Math.max(0, (score / MAX_SCORE) * 100))}%`;

export const AnalyzationSummary = () => {
  const { t } = useTranslation();
  const { teamId, surveyId } = useParams();

  const { data: summaryData, isLoading, error } = useAnalyzationSummaryQuery(
    Number(teamId),
    Number(surveyId),
  );
  const { data: analyzedSurvey } = useGetAnalyzeSurvey(Number(surveyId));
  const questions = analyzedSurvey?.data?.map((question) => question.question);
  const basePath = `/teams/${teamId}/analyze-survey/${surveyId}`;

  const layout = (content: React.ReactNode) => (
    <AnalysisLayout questions={questions} step="summary">
      {content}
    </AnalysisLayout>
  );

  if (!teamId || !surveyId) {
    return layout(
      <Alert data-color="danger">
        <Heading level={2} data-size="xs">
          {t('analyzationSummary.invalidTeamOrSurveyId')}
        </Heading>
        <Paragraph>{t('analyzationSummary.invalidUrlDescription')}</Paragraph>
      </Alert>,
    );
  }

  if (isLoading) {
    return layout(
      <div className={classes.loading} aria-busy="true">
        <GhostLoader style={{ width: '60%', height: 80 }} />
        <GhostLoader style={{ width: '100%', height: 140 }} />
        <GhostLoader style={{ width: '100%', height: 420 }} />
      </div>,
    );
  }

  if (error) {
    return layout(
      <Alert data-color="danger">
        <Heading level={2} data-size="xs">
          {error.name}
        </Heading>
        <Paragraph>{error.message}</Paragraph>
      </Alert>,
    );
  }

  if (!summaryData) {
    return layout(
      <Alert data-color="info">
        <Heading level={2} data-size="xs">
          {t('analyzationSummary.summaryComingNext')}
        </Heading>
        <Paragraph>{t('analyzationSummary.firstSurveyDescription')}</Paragraph>
        <Paragraph>
          <Link to={Routes.Dashboard}>{t('analyzationSummary.goToOverview')}</Link>
        </Paragraph>
      </Alert>,
    );
  }

  const comparison = summaryData.surveyScoreComparison;
  const rows = summaryData.SurveyQuestionScoreComparison ?? [];
  const downCount = rows.filter((row) => row.difference < 0).length;
  const title =
    comparison.scoreDifferenceDirection === 'NEGATIVE'
      ? t('analysis.summary.titleDown')
      : comparison.scoreDifferenceDirection === 'POSITIVE'
        ? t('analysis.summary.titleUp')
        : t('analysis.summary.titleSame');

  /** Link to the matching question, or null when the texts do not match */
  const questionLink = (question: string) => {
    const index = analyzedSurvey?.data?.findIndex(
      (item) => normalizeQuestion(item.question) === normalizeQuestion(question),
    );
    return index !== undefined && index >= 0 ? `${basePath}/${index + 1}` : null;
  };

  return layout(
    <div className={classes.summary}>
      <div className={classes.intro}>
        <span className={classes.eyebrow}>{t('analysis.summary.eyebrow')}</span>
        <Heading level={1} data-size="xl" className={classes.title}>
          {title}
        </Heading>
        {summaryData.overview && (
          <Paragraph data-size="lg" className={classes.lead}>
            {summaryData.overview}
          </Paragraph>
        )}
      </div>

      <section aria-label={t('analysis.summary.statsLabel')} className={classes.stats}>
        <div className={classes.stat}>
          <span className={classes.statLabel}>{t('analysis.summary.now')}</span>
          <span className={classes.statValue}>
            <span className={classes.bigNumber}>{formatScore(comparison.currentScore, 2)}</span>
            <span className={classes.statLabel}>{t('analysis.scoreOf')}</span>
          </span>
          <AgreementTag standardDeviation={comparison.currentStandardDeviation} />
        </div>
        <span aria-hidden="true" className={classes.divider} />
        <div className={classes.stat}>
          <span className={classes.statLabel}>{t('analysis.summary.previous')}</span>
          <span className={classes.statValue}>
            <span className={classes.bigNumber}>{formatScore(comparison.previousScore, 2)}</span>
            <span className={classes.statLabel}>{t('analysis.scoreOf')}</span>
          </span>
          <AgreementTag standardDeviation={comparison.previousStandardDeviation} />
        </div>
        <span aria-hidden="true" className={classes.divider} />
        <div className={classes.stat}>
          <span className={classes.statLabel}>{t('analysis.summary.change')}</span>
          <span className={classes.bigNumber} data-tone={changeTone(comparison.scoreDifference)}>
            {formatChange(comparison.scoreDifference, 2)}
          </span>
          {rows.length > 0 && (
            <span className={classes.statLabel}>
              {t('analysis.summary.downCount', { count: downCount, total: rows.length })}
            </span>
          )}
        </div>
      </section>

      <div className={classes.columns}>
        <section aria-labelledby="per-question-heading" className={classes.questions}>
          <div className={classes.questionsHeader}>
            <Heading id="per-question-heading" level={2} data-size="md">
              {t('analysis.summary.perQuestion')}
            </Heading>
            <span aria-hidden="true" className={classes.legend}>
              <span className={classes.legendItem}>
                <span className={classes.legendNow} />
                {t('analysis.summary.legendNow')}
              </span>
              <span className={classes.legendItem}>
                <span className={classes.legendPrevious} />
                {t('analysis.summary.legendPrevious')}
              </span>
            </span>
          </div>

          {rows.length > 0 ? (
            <>
              <ul className={classes.rows}>
                {rows.map((row) => {
                  const to = questionLink(row.question);
                  const content = (
                    <>
                      <span className={classes.rowHeader}>
                        <span className={classes.rowQuestion}>{row.question}</span>
                        <span className={classes.rowChange} data-tone={changeTone(row.difference)}>
                          {formatChange(row.difference)}
                        </span>
                      </span>
                      <span aria-hidden="true" className={classes.track}>
                        <span
                          className={classes.fill}
                          data-tone={levelTones[getScoreLevel(row.currentScore)]}
                          style={{ width: percent(row.currentScore) }}
                        />
                        <span className={classes.previousMarker} style={{ left: percent(row.previousScore) }} />
                      </span>
                      <span className={classes.rowMeta}>
                        <span className={classes.statLabel}>
                          {t('analysis.summary.values', {
                            now: formatScore(row.currentScore),
                            previous: formatScore(row.previousScore),
                          })}
                        </span>
                        <AgreementTag standardDeviation={row.currentStandardDeviation} />
                      </span>
                    </>
                  );
                  return (
                    <li key={row.id}>
                      {to ? (
                        <Link to={to} className={`${classes.row} ${classes.rowLink}`}>
                          {content}
                        </Link>
                      ) : (
                        <div className={classes.row}>{content}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
              <Paragraph data-size="sm" className={classes.subtle}>
                {t('analysis.summary.hint')}
              </Paragraph>
            </>
          ) : (
            <Alert data-color="info">
              <Heading level={3} data-size="2xs">
                {t('analyzationSummaryBody.summaryComingNext')}
              </Heading>
              <Paragraph>{t('analyzationSummaryBody.summaryComingNextDescription')}</Paragraph>
            </Alert>
          )}
        </section>

        <aside aria-labelledby="talk-about-heading" className={classes.aside}>
          {summaryData.areasOfChange.length > 0 && (
            <div className={classes.findings}>
              <Heading id="talk-about-heading" level={2} data-size="sm">
                {t('analysis.summary.talkAbout')}
              </Heading>
              <ul className={classes.findingList}>
                {summaryData.areasOfChange.map((area) => (
                  <li key={area.id}>
                    {area.title && (
                      <Heading level={3} data-size="2xs">
                        {area.title}
                      </Heading>
                    )}
                    <Paragraph data-size="sm" className={classes.subtle}>
                      {area.description}
                    </Paragraph>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Button asChild variant="secondary">
            <Link to={buildRoute(Routes.InterpretiveReport, { teamId, surveyId })}>
              {t('analysis.summary.interpretiveReport')}
            </Link>
          </Button>
        </aside>
      </div>

      <div className={classes.footer}>
        <Button asChild variant="secondary">
          <Link to={`${basePath}/${questions?.length || 1}`}>{t('analysis.previous')}</Link>
        </Button>
        <Button asChild>
          <Link to={Routes.Dashboard}>{t('analysis.summary.finish')}</Link>
        </Button>
      </div>
    </div>,
  );
};
